<template>
	<!--
		Delphi ts_Impressora / ComboBoxImpressoras (altura ~21px, fonte normal).
		Lista compacta — sem select nativo (fonte grande no mobile quebra o layout).
	-->
	<div class="impressora-direcionada flex flex-column w-full p-2 gap-2">
		<div class="impressora-direcionada__aviso text-center text-red-600 font-semibold">
			Ao selecionar uma impressora abaixo a mesma terá prioridade sobre o
			cadastro padrão de impressoras para impressão de acondicionamento
			antecipado
		</div>

		<div class="impressora-direcionada__sel flex flex-column gap-1 w-full">
			<label class="impressora-direcionada__label">
				Impressora p/ Antecipação do Acondicionamento
			</label>

			<div v-if="carregando" class="impressora-direcionada__hint">
				Carregando impressoras...
			</div>

			<!-- Combo compacto estilo Delphi (TComboBox) -->
			<Dropdown
				v-else
				v-model="impressoraSelecionada"
				:options="opcoesDropdown"
				option-label="label"
				option-value="value"
				placeholder="(padrão do cadastro)"
				class="impressora-direcionada__combo w-full"
				:show-clear="!!impressoraSelecionada"
				:filter="opcoesImpressora.length > 8"
				filter-placeholder="Filtrar..."
				:empty-message="erroLista || 'Nenhuma impressora listada'"
				panel-class="impressora-direcionada__panel"
				@change="onComboChange"
			/>

			<small v-if="erroLista" class="text-orange-600 text-xs">
				{{ erroLista }}
			</small>
			<small v-else-if="!carregando && opcoesImpressora.length === 0" class="text-600 text-xs">
				Nenhuma impressora no servidor. Configure config/impressoras.json ou IMPRESSORAS_WMS.
			</small>
		</div>

		<div class="flex flex-row justify-content-start mt-2 gap-2">
			<R3Button
				label="Voltar"
				icon="pi pi-arrow-left"
				severity="secondary"
				class="p-button-sm"
				@click="voltarRecebimento"
			/>
			<R3Button
				label="Atualizar"
				icon="pi pi-refresh"
				class="p-button-sm"
				:disabled="carregando"
				@click="carregarImpressoras"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
	import type { FPROC_DIGITACAO_PLANILHA_CONFERENCIA_COLETOR } from "~/classes/PROC_DIGITACAO_PLANILHA_CONFERENCIA_COLETOR/FPROC_DIGITACAO_PLANILHA_CONFERENCIA_COLETOR";
	import type { R3HTTPResponse } from "@r3shared/types/http";

	type ListarImpressorasResponse = {
		impressoras: Array<{ nome: string }>;
		origem: string;
	};

	const props = defineProps<{
		parent: FPROC_DIGITACAO_PLANILHA_CONFERENCIA_COLETOR;
	}>();

	const store = useConferenciaPlanilhaCegaStore();
	const { $r3fetchdata } = useNuxtApp();

	const impressoraSelecionada = ref<string | null>(
		store.impressoraDirecionada ? String(store.impressoraDirecionada) : null,
	);
	const opcoesImpressora = ref<string[]>([]);
	const carregando = ref(false);
	const erroLista = ref("");
	const origemLista = ref("");

	/** Opções do combo: valor null = padrão do cadastro (Delphi ItemIndex vazio). */
	const opcoesDropdown = computed(() => {
		const itens = opcoesImpressora.value.map((nome) => ({
			label: nome,
			value: nome,
		}));
		return [
			{ label: "(padrão do cadastro)", value: null as string | null },
			...itens,
		];
	});

	watch(
		() => store.impressoraDirecionada,
		(v) => {
			impressoraSelecionada.value = v ? String(v) : null;
		},
	);

	function extrairLista(raw: unknown): { nomes: string[]; origem: string } {
		if (!raw || typeof raw !== "object") {
			return { nomes: [], origem: "" };
		}
		const env = raw as {
			response?: ListarImpressorasResponse;
			impressoras?: Array<{ nome: string } | string>;
			origem?: string;
		};
		const body = env.response ?? (env as unknown as ListarImpressorasResponse);
		const lista = body?.impressoras ?? env.impressoras ?? [];
		const nomes = (Array.isArray(lista) ? lista : [])
			.map((item) => {
				if (typeof item === "string") return item.trim();
				if (item && typeof item === "object" && "nome" in item) {
					return String((item as { nome: string }).nome ?? "").trim();
				}
				return "";
			})
			.filter(Boolean);
		return { nomes, origem: body?.origem ?? env.origem ?? "" };
	}

	async function carregarImpressoras() {
		carregando.value = true;
		erroLista.value = "";
		try {
			const raw = await $r3fetchdata.get<
				R3HTTPResponse<ListarImpressorasResponse> | ListarImpressorasResponse
			>("/api/modu_controle/listarimpressoras");

			const { nomes, origem } = extrairLista(raw);
			opcoesImpressora.value = nomes;
			origemLista.value = origem;

			for (const n of nomes) {
				store.adicionarImpressoraDisponivel(n);
			}

			if (
				impressoraSelecionada.value
				&& nomes.length > 0
				&& !nomes.includes(impressoraSelecionada.value)
			) {
				impressoraSelecionada.value = null;
				store.setImpressoraDirecionada("");
			}
		}
		catch (e) {
			console.error("listarimpressoras:", e);
			erroLista.value = "Falha ao listar impressoras. Tente Atualizar.";
			const sessao = store.impressorasDisponiveis;
			opcoesImpressora.value = Array.isArray(sessao) ? [...sessao] : [];
		}
		finally {
			carregando.value = false;
		}
	}

	function onComboChange() {
		const nome = impressoraSelecionada.value
			? String(impressoraSelecionada.value).trim()
			: "";
		store.setImpressoraDirecionada(nome);
		if (nome) {
			store.adicionarImpressoraDisponivel(nome);
		}
	}

	function voltarRecebimento() {
		props.parent.changeActiveTab("recebimento");
	}

	onMounted(() => {
		void carregarImpressoras();
	});
</script>

<style scoped>
	.impressora-direcionada {
		/* Conteúdo no topo, compacto como no Delphi (alTop) */
		max-width: 22rem;
		margin: 0 auto;
		background: #fff;
	}

	.impressora-direcionada__aviso {
		font-size: 0.8rem;
		line-height: 1.3;
		padding: 0.35rem 0.25rem 0.5rem;
	}

	.impressora-direcionada__label {
		font-size: 0.75rem;
		line-height: 1.2;
		color: #333;
	}

	.impressora-direcionada__hint {
		font-size: 0.8rem;
		color: #666;
		padding: 0.25rem 0;
	}

	/* Combo fechado — altura/fonte próximas do TComboBox (Height=21) */
	.impressora-direcionada__combo :deep(.p-dropdown) {
		min-height: 1.75rem;
		height: 1.75rem;
		font-size: 0.8rem;
		align-items: center;
	}

	.impressora-direcionada__combo :deep(.p-dropdown-label) {
		padding: 0.2rem 0.45rem;
		font-size: 0.8rem;
		line-height: 1.35;
	}

	.impressora-direcionada__combo :deep(.p-dropdown-trigger) {
		width: 1.75rem;
	}

	.impressora-direcionada__combo :deep(.p-dropdown-clear-icon) {
		font-size: 0.7rem;
	}
</style>

<!--
	Painel do dropdown é teleported no body — precisa de estilo global
	com classe panel-class.
-->
<style>
	.impressora-direcionada__panel.p-dropdown-panel {
		font-size: 0.8rem !important;
		max-width: min(92vw, 22rem);
	}

	.impressora-direcionada__panel .p-dropdown-items {
		padding: 0.15rem 0;
	}

	.impressora-direcionada__panel .p-dropdown-item {
		font-size: 0.8rem !important;
		line-height: 1.25 !important;
		padding: 0.3rem 0.5rem !important;
		min-height: 1.6rem;
	}

	.impressora-direcionada__panel .p-dropdown-header {
		padding: 0.3rem 0.45rem;
	}

	.impressora-direcionada__panel .p-dropdown-filter {
		font-size: 0.8rem;
		padding: 0.25rem 0.4rem;
	}

	.impressora-direcionada__panel .p-dropdown-empty-message {
		font-size: 0.8rem;
		padding: 0.4rem 0.5rem;
	}
</style>
